import { DownloadIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Flex,
  Text
} from "@chakra-ui/react"
import { useState } from "react"
import { useNavigate } from "react-router"
import bg from "../assets/images/installation_bg.jpg"
import { Paths, breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel, pageContentWidth } from "../common/commonConstants"
import { buildDocumentUrl } from "../common/documentUrl"
import { SubPage, SubPageContent } from "../components/SubPage"
import { Heading3PT } from "../components/commons/Heading3PT"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { ErrorMessage } from "../components/commons/error.main"
import { Loading } from "../components/commons/loader.main"
import { StaticPageHeader } from "../components/commons/static-page-header"
import { PDFIcon } from "../components/productInfo/product-info.main"
import { Colors } from "../theme/colors"
import { useReportError, verifyPdfAccessible } from "../common/commonHooks/useReportError"
import { useGetInstallationInstructionReportLazyQuery, useProductInstallationInstructionsQuery } from "../type-and-hooks"

export const InstallationInstructions = (): JSX.Element => {
  const { loading, error, data } = useProductInstallationInstructionsQuery()
  const [getInstallationReport] = useGetInstallationInstructionReportLazyQuery({ fetchPolicy: 'network-only' })
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const { reportError } = useReportError()
  const navigate = useNavigate()

  const fetchReportUrl = async (instructionNo: string): Promise<string | null> => {
    const { data } = await getInstallationReport({ variables: { instruction: instructionNo } })
    return data?.getInstallationInstructionReport?.reportURL ?? null
  }

  const handleDirectDownload = async (instructionNo: string, name: string): Promise<void> => {
    setActionLoading(`download-${instructionNo}`)
    try {
      const { data } = await getInstallationReport({ variables: { instruction: instructionNo } })
      const url = data?.getInstallationInstructionReport?.reportURL ?? null
      const downloadUrl = data?.getInstallationInstructionReport?.downloadURL ?? url
      if (!url) throw new Error("URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Installation instruction "${name}" returned access denied` })
        return
      }
      window.open(downloadUrl ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Installation instruction "${name}" failed to download` })
    } finally {
      setActionLoading(null)
    }
  }

  const handlePreview = async (instructionNo: string, name: string): Promise<void> => {
    setActionLoading(`preview-${instructionNo}`)
    try {
      const { data } = await getInstallationReport({ variables: { instruction: instructionNo } })
      const url = data?.getInstallationInstructionReport?.reportURL ?? null
      if (!url) throw new Error("URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Installation instruction "${name}" returned access denied` })
        return
      }
      window.open(buildDocumentUrl(data?.getInstallationInstructionReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Installation instruction "${name}" failed to load` })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <>
      <Loading loading={loading} />
      {error && <ErrorMessage error={"Failed to load: Product Info Table."} primaryAction={false} primaryActionText={""} />}
      {data && (
        <SubPage>
          <Box margin="auto">
            <StaticPageHeader title="Installation Instructions and Data Sheets" bg={bg} />
            <BreadCrumb
              options={{
                ...breadCrumbFirstLevel,
                ...breadCrumbReportsAndResourcesSecondLevel,
                thirdLevel: { title: "Installation Instructions and Data Sheets" }
              }}
            />
            <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "70%", "70%"] }}>
              <Flex align={"center"} mr={[0, 0, "4px"]} flexDir={"column"}>
                <Box maxW="580px" textAlign={["left", "left", "center"]} margin="auto">
                  <Text variant={["RBody", "RBody", "Body1Regular"]}>
                    These instructions are to be used as a <span style={{ fontWeight: "bold", color: Colors.Secondary.c2 }}>guide only.</span>
                  </Text>
                  <Text variant={["RBody", "RBody", "Body1Regular"]} mt={["12px", "12px", "9px"]} ml={[0, 0, "20px"]}>
                    To comply with the National Construction Code as a Deemed-to-Satisfy solution, the installation of the fire collars must be
                    identical to the installation in the test or assessment report.
                  </Text>
                  <Text variant={["RBody", "RBody", "Body1Regular"]} mt={["12px", "12px", "9px"]} ml={[0, 0, "20px"]}>
                    Certifiers and installers should refer to these reports for the details.
                  </Text>
                  <Box mt={["12px", "12px", "18px"]} ml={[0, 0, "20px"]}>
                    <Text variant={["RBody", "RBody", "Body1Regular"]} as="span">
                      Links to the appropriate reports can also be found on the{" "}
                    </Text>
                    <Button variant="linkButtonBig" onClick={() => { navigate(Paths.Product.all) }}>
                      Product Pages
                    </Button>
                    <Text variant={["RBody", "RBody", "Body1Regular"]} as="span">.</Text>
                  </Box>
                </Box>

                <Box w={["100%", "100%", "auto"]} px={[4, 4, 0]} textAlign={"center"}>
                  <Heading3PT text="Collar Instructions and Data" props={{ mt: ["32px", "32px", "20px"] }} />
                  <Box mt={["16px", "16px", "24px"]} ml="0">
                    <Flex flexWrap="wrap" justifyContent="center" gap={["16px", "16px", "24px"]} mx="auto">
                      {data?.productInstallationInstructions?.map((instructionGroup, groupIndex) => (
                        <Box
                          key={`${instructionGroup?.installation_type}-${groupIndex}`}
                          flex={["1 1 100%", "1 1 100%", "0 1 calc(50% - 12px)"]}
                          minW={["100%", "100%", "400px"]}
                          maxW={["100%", "100%", "500px"]}
                          mb="24px"
                          p="16px"
                          bg="white"
                        >
                          {instructionGroup?.installation_type && (
                            <Heading3PT
                              text={instructionGroup.installation_type}
                              props={{
                                mt: "0",
                                mb: "16px",
                                fontSize: ["18px", "18px", "20px"],
                                color: Colors.Primary.c1,
                                textAlign: "center",
                                w: "100%"
                              }}
                            />
                          )}
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Box>
                              {instructionGroup?.files?.map((file, fileIndex) => (
                                <Flex key={`${file?.reportName}-${fileIndex}`} mt="10px" align="center" gap="4px">
                                  <Button
                                    variant="linkButton"
                                    p="0"
                                    minW="0"
                                    h="auto"
                                    display="flex"
                                    alignItems="center"
                                    flexShrink={0}
                                    onClick={() => { void handleDirectDownload(file?.reportURL ?? "", file?.reportName ?? "") }}
                                    isLoading={actionLoading === `download-${file?.reportURL}`}
                                    aria-label={`Download ${file?.reportName ?? ""}`}
                                  >
                                    <DownloadIcon />
                                  </Button>
                                  <Button
                                    variant="linkButton"
                                    ml="3px"
                                    minH="22px"
                                    fontSize="15px"
                                    textAlign="left"
                                    lineHeight="18px"
                                    fontWeight="400"
                                    whiteSpace="unset"
                                    wordBreak="break-word"
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="flex-start"
                                    onClick={() => { void handlePreview(file?.reportURL ?? "", file?.reportName ?? "") }}
                                    isLoading={actionLoading === `preview-${file?.reportURL}`}
                                  >
                                    <PDFIcon />
                                    <Text variant={["RButton2", "RButton2", "Button2Big"]} as="span" paddingLeft="8px">{file?.reportName}</Text>
                                  </Button>
                                </Flex>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                </Box>
              </Flex>
            </SubPageContent>
          </Box>
        </SubPage>
      )}
    </>
  )
}
